import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowRight, Banknote, Users, MapPin, Package, 
  RefreshCw, Calendar, Calculator, CheckCircle
} from 'lucide-react';

const TermLoansPage = () => {
  const useCases = [
    { 
      icon: Users, 
      title: "Hiring Crews", 
      description: "Expand your workforce to take on more contracts and grow revenue" 
    },
    { 
      icon: MapPin, 
      title: "Expanding Service Areas", 
      description: "Enter new markets and reach more customers" 
    },
    { 
      icon: Package, 
      title: "Purchasing Materials", 
      description: "Stock up on supplies for large projects or busy seasons" 
    },
    { 
      icon: RefreshCw, 
      title: "Refinancing Obligations", 
      description: "Consolidate existing debt with better terms" 
    },
    { 
      icon: Calendar, 
      title: "Managing Seasonal Cash Flow", 
      description: "Bridge the gap during slower months" 
    },
    { 
      icon: Calculator, 
      title: "Stabilizing Operations", 
      description: "Invest in infrastructure and systems" 
    },
  ];

  const benefits = [
    "Fixed monthly payments for predictable budgeting",
    "Know exactly when your loan will be paid off",
    "Structured repayment that fits your cash flow",
    "Competitive rates for qualified businesses",
    "Larger funding amounts for significant investments",
    "Build business credit with on-time payments"
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Banknote className="h-4 w-4" />
                Term Loans
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Term Loans for Landscaping Companies
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Structured financing with fixed payments and predictable repayment schedules. Get the capital you need to grow or stabilize your landscaping operations.
              </p>
              <Link to="/apply">
                <Button size="lg" className="bg-primary hover:bg-primary/90">
                  Apply Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Calculator className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Fixed Payments</h3>
                  <p className="text-gray-500">Predictable monthly obligations</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Loan Amount</span>
                    <span className="text-xl font-bold text-gray-900">$200,000</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Interest Rate</span>
                  <span className="text-lg font-semibold text-gray-900">9.5% APR</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Term Length</span>
                  <span className="text-lg font-semibold text-gray-900">10 Years (120 Months)</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-700">Monthly Payment</span>
                  <span className="text-xl font-bold text-blue-700">$2,586</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Can You Do With a Term Loan?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Term loans provide the structured capital landscaping companies need to invest in growth and stability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <Card key={index} className="border-gray-100">
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <useCase.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{useCase.title}</h3>
                      <p className="text-gray-600 text-sm">{useCase.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Term Loan Parameters</h3>
              <table className="w-full">
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 text-gray-600">Loan Amount</td>
                    <td className="py-3 text-right font-semibold text-gray-900">$25,000 - $500,000</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-gray-600">Term Length</td>
                    <td className="py-3 text-right font-semibold text-gray-900">1 - 10 years</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-gray-600">Interest Rates</td>
                    <td className="py-3 text-right font-semibold text-gray-900">Starting at 8.5% APR</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-gray-600">Payment Frequency</td>
                    <td className="py-3 text-right font-semibold text-gray-900">Monthly</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-gray-600">Payment Type</td>
                    <td className="py-3 text-right font-semibold text-gray-900">Fixed</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-sm text-gray-500 mt-4">*Terms and rates vary based on qualification and creditworthiness.</p>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Structured Financing for Growth
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Unlike revolving credit, term loans provide a lump sum with a clear repayment schedule. You'll know exactly what you owe each month and when your loan will be paid off.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Predictable Process</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Apply</h3>
              <p className="text-gray-600 text-sm">Quick application with minimal paperwork</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Approved</h3>
              <p className="text-gray-600 text-sm">Fast decisions based on your business</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Receive Funds</h3>
              <p className="text-gray-600 text-sm">Lump sum deposited to your account</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Repay Monthly</h3>
              <p className="text-gray-600 text-sm">Fixed payments until paid in full</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Apply for a term loan and get the structured financing you need to take your landscaping company to the next level.
          </p>
          <Link to="/apply">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default TermLoansPage;
